import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { PageHeader } from "../components/ui/page-header";
import { TabNavigation } from "../components/ui/tab-navigation";
import {
  Plus,
  Edit,
  Calendar,
  UserIcon,
  ArrowLeft,
  Search,
  Eye,
  FolderOpen,
  FileStack,
} from "lucide-react";
import { useAuth } from "../App";
import {
  useEnhancedProjectsQuery,
  GetEnhancedProjectsParams,
  EnhancedProject,
} from "../api/projectManagementApi";
import { useLTIContextsQuery, LTIContext } from "../api/ltiApi";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import paths from "../routes";
import { useDebounce } from "../hooks/useDebounce";
import { useStaffMemberQuery } from "../api/createProjectApi";

export default function ViewAllProjectsPage() {
  const [activeTab, setActiveTab] = useState<"all-projects" | "my-projects">(
    "all-projects"
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [courseFilter, setCourseFilter] = useState<string | undefined>(
    undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [difficultyFilter, setDifficultyFilter] = useState<string | undefined>(
    undefined
  );
  const [projectTypeFilter, setProjectTypeFilter] = useState<
    string | undefined
  >(undefined);
  const [isLinkedFilter, setIsLinkedFilter] = useState<string | undefined>(
    undefined
  );
  const [createdBy, setCreatedBy] = useState<string | undefined>(undefined);

  // Fetch courses
  const { data: courses = [], isLoading: coursesLoading } = useLTIContextsQuery(
    { enabled: true, forLinking: true }
  );
  const typedCourses = courses as LTIContext[];

  // Fetch staff members based on selected course
  const {
    data: courseStaff,
    isLoading: staffLoading,
    refetch,
  } = useStaffMemberQuery(courseFilter || "", Boolean(courseFilter));

  useEffect(() => {
    if (courseFilter) refetch();
  }, [courseFilter, refetch]);

  const staffData = [
    ...(courseStaff?.instructors || []),
    ...(courseStaff?.tas || []),
  ];

  // Debounce the input value for API calls
  const debouncedSearchQuery = useDebounce(inputValue, 1000);

  // Sync searchQuery with debounced value
  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
    setPage(1);
  }, [debouncedSearchQuery]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  // Sync inputValue when searchQuery changes externally
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const projectQueryParams: GetEnhancedProjectsParams = {
    page,
    limit,
    ...(searchQuery ? { search: searchQuery } : {}),
    ...(courseFilter ? { courseId: courseFilter } : {}),
    ...(statusFilter ? { status: statusFilter as any } : {}),
    ...(createdBy ? { createdBy: createdBy as any } : {}),
    ...(difficultyFilter ? { difficultyLevel: difficultyFilter as any } : {}),
    ...(projectTypeFilter ? { type: projectTypeFilter as any } : {}),
    ...(isLinkedFilter && isLinkedFilter !== "all"
      ? { isLinked: isLinkedFilter === "linked" }
      : {}),
  };

  const {
    data: enhancedProjectsData,
    isLoading: projectsLoading,
    isError: projectsError,
  } = useEnhancedProjectsQuery(projectQueryParams);

  // Extract projects and pagination from the API response
  const projects = enhancedProjectsData?.projects || [];
  const pagination = enhancedProjectsData?.pagination || {};

  const navigate = useNavigate();
  const navigateBack = () => {
    navigate(paths.root);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <PageHeader
        title="All Projects"
        subtitle="Manage and view all projects across the platform"
      >
        <Button
          className="bg-bits-blue hover:bg-blue-700"
          onClick={() => navigate(paths.createProject)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Project
        </Button>
      </PageHeader>

      {/* Tabs */}
      <TabNavigation
        tabs={[
          {
            key: "all-projects",
            label: "All Projects",
            icon: <FolderOpen className="h-4 w-4" />,
          },
          {
            key: "my-projects",
            label: "My Projects",
            icon: <FileStack className="h-4 w-4" />,
          },
        ]}
        activeTab={activeTab}
        onTabChange={(tab) =>
          setActiveTab(tab as "all-projects" | "my-projects")
        }
        className="mb-6"
      />

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-40 w-80">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 bg-bits-grey-100" />
          <Input
            placeholder="Search Projects..."
            className="pl-10 bg-bits-grey-100 text-bits-grey-600"
            value={inputValue}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          />
        </div>

        <Select
          value={courseFilter || "all-courses"}
          onValueChange={(v) => {
            setCourseFilter(v === "all-courses" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-auto sm:min-w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="text-md">
            <SelectItem value="all-courses">All courses</SelectItem>
            {coursesLoading ? (
              <SelectItem value="loading" disabled>
                Loading courses...
              </SelectItem>
            ) : (
              typedCourses.map((course) => (
                <SelectItem key={course.id} value={course.id}>
                  {course.contextTitle}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        <Select
          value={createdBy || "all-creators"}
          onValueChange={(v) => {
            setCreatedBy(v === "all-creators" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="text-md">
            <SelectItem value="all-creators">Select Instructors</SelectItem>
            {!courseFilter ? (
              <SelectItem value="loading" disabled>
                Please select a course
              </SelectItem>
            ) : staffLoading ? (
              <SelectItem value="loading" disabled>
                Loading instructors...
              </SelectItem>
            ) : (
              staffData?.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  {staff.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        <Select
          value={isLinkedFilter || "all"}
          onValueChange={(v) => {
            setIsLinkedFilter(v === "all" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            <SelectItem value="linked">Linked projects</SelectItem>
            <SelectItem value="unlinked">Unlinked projects</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter || "all-status"}
          onValueChange={(v) => {
            setStatusFilter(v === "all-status" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-status">All status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={difficultyFilter || "all-difficulties"}
          onValueChange={(v) => {
            setDifficultyFilter(v === "all-difficulties" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-difficulties">All Difficulties</SelectItem>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={projectTypeFilter || "all-types"}
          onValueChange={(v) => {
            setProjectTypeFilter(v === "all-types" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-types">All Types</SelectItem>
            <SelectItem value="group">Group</SelectItem>
            <SelectItem value="individual">Individual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {projectsLoading ? (
        <div className="text-center py-12 text-bits-grey-600">
          Loading projects...
        </div>
      ) : projectsError ? (
        <div className="text-center py-12 text-bits-red text-sm">
          Failed to load projects
        </div>
      ) : !projects || projects.length === 0 ? (
        <div className="text-center py-12 text-bits-grey-600 text-sm">
          No projects found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 text-lg font-semibold gap-6">
          {projects.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && projects.length > 0 && (
        <div className="flex items-center justify-between pt-6 mt-6">
          <div className="text-sm text-bits-grey-600">
            Page {pagination.currentPage} of {pagination.totalPages} •{" "}
            {pagination.totalItems} items
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || projectsLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages || projectsLoading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
            <Select
              value={String(limit)}
              onValueChange={(v) => {
                setLimit(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-24 bg-bits-grey-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 50].map((l) => (
                  <SelectItem key={l} value={String(l)}>
                    {l} / page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

function CourseCard({ course }: { course: EnhancedProject }) {
  const navigate = useNavigate();

  function getDateDifference(
    startDate?: string | null,
    dueDate?: string | null
  ) {
    if (!startDate && !dueDate) return "Dates not available";

    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const due = dueDate ? new Date(dueDate) : null;

    let diffDays = 0;
    if (due) {
      diffDays = Math.ceil(
        (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let statusString = "On Track";
    if (diffDays < 0) statusString = "Overdue";
    else if (diffDays === 0) statusString = "Due today";
    else if (diffDays === 1) statusString = "1 day left";
    else if (diffDays > 1) statusString = diffDays + " days left";

    const formatDate = (d: Date | null) =>
      d
        ? `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`
        : "N/A";

    const dateValue =
      start && due
        ? `${formatDate(start)} - ${formatDate(due)}`
        : start
        ? `${formatDate(start)}`
        : due
        ? `${formatDate(due)}`
        : "Dates not available";

    return `${dateValue} (${statusString})`;
  }

  // const submissionRate =
  //   ((course.students - course.pendingGrades) / course.students) * 100;

  // const submissions = `${assignment.submissions} / ${assignment.totalStudents}`;
  // const graded = `${assignment.gradedSubmissions} / ${assignment.submissions}`;
  const navigateToEdit = (id: string) => {
    navigate(`${paths.editProject.replace(":projectId", id)}?mode=edit`);
  };
  console.log(course);

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200"
      onClick={() =>
        navigate(
          `${paths.instructorProjectDetails.replace(":projectId", course.id)}`
        )
      }
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex gap-4 items-start justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-bits-grey">
                <div className="flex justify-between items-center capitalize">
                  <div>{course?.title}</div>
                  <div
                    className={`ml-4 text-xs px-2 py-1 rounded-md text-center font-medium w-fit
                      ${
                        course?.status?.toLowerCase().includes("active")
                          ? "bg-yellow-100 text-yellow-800"
                          : course?.status?.toLowerCase().includes("draft")
                          ? "bg-gray-700 text-white"
                          : course?.status?.toLowerCase().includes("close")
                          ? "bg-red-600 text-white"
                          : course?.status?.toLowerCase().includes("deadline")
                          ? "bg-red-600 text-white"
                          : course?.status?.toLowerCase().includes("archive")
                          ? "bg-orange-500 text-white"
                          : course?.status?.toLowerCase().includes("complete")
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                  >
                    {course?.status}
                  </div>
                </div>
              </CardTitle>
              <CardDescription>
                <div className="text-sm font-normal text-bits-grey-600">
                  {course?.course?.code}
                </div>
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-4 items-start justify-between">
            {course?.status?.toLowerCase() === "active" ? (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `${paths.instructorProjectDetails.replace(
                      ":projectId",
                      course.id
                    )}`
                  );
                }}
                className="border-text-bits-grey-300 z-20"
                variant="outline"
              >
                <Eye className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToEdit(course.id);
                }}
                className="border-text-bits-grey-300 z-20"
                variant="outline"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {(course as any)?.totalStudentCount || 0}
              </div>
              <div className="text-bits-grey-600 font-normal text-sm">
                Total Students
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {course?.submissionCount}
              </div>
              <div className="text-bits-grey-600 font-normal text-sm">
                Submissions
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {(course as any)?.gradedStudentCount || 0}
              </div>
              <div className="text-bits-grey-600 font-normal text-sm">
                Graded
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-md font-medium text-bits-grey-600 mb-2">
              <span>Progress</span>
              <span>
                {(course as any)?.totalStudentCount > 0
                  ? `${(
                      ((course?.submissionCount || 0) /
                        (course as any)?.totalStudentCount) *
                      100
                    ).toFixed(0)}%`
                  : "0%"}
              </span>
            </div>
            <Progress
              value={
                (course as any)?.totalStudentCount > 0
                  ? ((course?.submissionCount || 0) /
                      (course as any)?.totalStudentCount) *
                    100
                  : 0
              }
              className="h-2"
            />
          </div>

          <div className="flex items-center gap-20 text-sm">
            <div className="flex items-center space-x-1 justify-between">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-bits-grey-600 text-sm font-semibold">
                {getDateDifference(course?.start_date, course?.due_date)}
              </span>
            </div>
            {/* <div className="flex items-start justify-between">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <div className="text-muted-foreground">Your Role:</div>
              <div className="text-sm font-semibold ml-1 text-bits-grey">
                {useAuth().user?.role}
              </div>
            </div> */}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
